// components/RaceTimePredictor.tsx

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import TooltipAndIcon from "components/Tooltip/TooltipAndIcon";

import {
  calculatePace,
  calculatorFieldProps,
  distanceUnits,
  eventDistances,
} from "./PaceCalculator";

interface RaceTimePredictorProps {
  distance: number | string;
  distanceUnit: "km" | "miles" | "meters";
  paceUnit: "km" | "mile";
  hours: string;
  minutes: string;
  seconds: string;
}

const convertTimeToSeconds = (
  hours: string,
  minutes: string,
  seconds: string
) => {
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

export const convertSecondsToTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);
  return { hours, minutes, seconds };
};
export const calculatePredictedTime = (
  currentDistance: number,
  currentTimeInSeconds: number,
  targetDistance: number,
  algorithm: "riegel" | "newman",
  returnSeconds?: boolean
) => {
  let predictedTimeInSeconds: number;

  if (algorithm === "riegel") {
    predictedTimeInSeconds =
      currentTimeInSeconds * Math.pow(targetDistance / currentDistance, 1.06);
  } else if (algorithm === "newman") {
    // WARNING - THIS WAS HALLUCINATED BY CHATGPT!!
    const k = 1.07; // You can adjust this exponent based on distance ranges
    predictedTimeInSeconds =
      currentTimeInSeconds * Math.pow(targetDistance / currentDistance, k);
  } else {
    throw new Error("Unsupported algorithm");
  }

  return returnSeconds
    ? predictedTimeInSeconds
    : convertSecondsToTime(predictedTimeInSeconds);
};
const calculateConfidence = (
  currentDistance: number,
  targetDistance: number
) => {
  const ratio =
    Math.min(currentDistance, targetDistance) /
    Math.max(currentDistance, targetDistance);
  const decayFactor = 0.5; // Adjust this factor to control the decay rate
  const confidence = Math.exp(-decayFactor * (1 - ratio)) * 100;
  return Math.round(confidence.toFixed(2));
};

const normalizeDistance = (distance: number, unit: string) => {
  if (unit === "miles") return distance * 1.60934;
  if (unit === "meters") return distance / 1000;
  if (unit === "yards") return distance * 0.0009144;
  return distance;
};
const getSortedEventDistances = (
  currentDistance: number,
  distanceUnit: string,
  existingDistances
) => {
  const normalizedDistance = normalizeDistance(currentDistance, distanceUnit);
  const sortedDistances = existingDistances || [...eventDistances];

  const existingIndex = sortedDistances.findIndex(
    (event) =>
      normalizeDistance(event.value.distance, event.value.unit) ===
      normalizedDistance
  );

  if (existingIndex === -1) {
    sortedDistances.push({
      label: `${currentDistance} ${distanceUnit}`,
      value: { distance: currentDistance, unit: distanceUnit },
    });
  }

  return sortedDistances.sort((a, b) => {
    const distanceA = normalizeDistance(a.value.distance, a.value.unit);
    const distanceB = normalizeDistance(b.value.distance, b.value.unit);
    return distanceA - distanceB;
  });
};

const RaceTimePredictor: React.FC<RaceTimePredictorProps> = ({
  distance,
  distanceUnit,
  paceUnit,
  hours,
  minutes,
  seconds,
}) => {
  const [open, setOpen] = React.useState<boolean>(false);

  const [algorithm, setAlgorithm] = React.useState<"riegel" | "newman">(
    "riegel"
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const currentDistanceInKm =
    distanceUnit === "miles"
      ? Number(distance) * 1.60934
      : distanceUnit === "meters"
      ? Number(distance) / 1000
      : Number(distance);

  const currentTimeInSeconds = convertTimeToSeconds(hours, minutes, seconds);

  /* const sortedEventDistances = getSortedEventDistances(
    Number(distance),
    distanceUnit
  ); */

  const [sortedEventDistances, setSortedEventDistances] = React.useState(
    getSortedEventDistances(Number(distance), distanceUnit)
  );

  return (
    <>
      {/* <TextField
        select
        label={
          <>
            Prediction algorithm{" "}
            <TooltipAndIcon title="There are different mathematical formulas that can be used to predict race performance. Riegal and Newman's are the two most widely used."></TooltipAndIcon>
          </>
        }
        value={algorithm}
        onChange={(event) => setAlgorithm(event.target.value)}
        sx={{ width: "220px", paddingBottom: 2 }}
      >
        {[
          { value: "riegel", label: "Riegel (recommended)" },
          { value: "newman", label: "Newman's" },
        ].map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </TextField>{" "} */}
      <Button
        variant="outlined"
        color="primary"
        onClick={handleClickOpen}
        startIcon={<AddIcon />}
      >
        Add additional distance(s)
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          sx: { minWidth: "375px" },

          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const formJson = Object.fromEntries((formData as any).entries());

            const { unit: newUnit, distance: newDistance } = formJson;

            setSortedEventDistances(
              getSortedEventDistances(
                Number(newDistance),
                newUnit,
                sortedEventDistances
              )
            );
            handleClose();
          },
        }}
      >
        <DialogTitle>Add distance to prediction</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xxs={6}>
              <TextField
                {...calculatorFieldProps}
                label="Distance"
                id="distance"
                name="distance"
                required
                autoFocus
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ minWidth: "40px" }}
              />
            </Grid>
            <Grid item xxs={6}>
              <TextField
                select
                {...calculatorFieldProps}
                defaultValue={"km"}
                label="Unit"
                id="unit"
                name="unit"
              >
                {distanceUnits.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="gray">
            Cancel
          </Button>
          <Button type="submit">Add</Button>
        </DialogActions>
      </Dialog>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Event</TableCell>
              <TableCell align="left">Predicted Time</TableCell>
              <TableCell align="left">Pace</TableCell>
              <TableCell align="right">
                Confidence
                <TooltipAndIcon title="The closer the provided distance and race distances, the greater the confidence of the prediction"></TooltipAndIcon>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEventDistances.map((event) => {
              const eventDistanceInKm =
                event.value.unit === "miles"
                  ? event.value.distance * 1.60934
                  : event.value.unit === "meters"
                  ? event.value.distance / 1000
                  : event.value.distance;

              const predictedTime = calculatePredictedTime(
                currentDistanceInKm,
                currentTimeInSeconds,
                eventDistanceInKm,
                algorithm
              );
              /*     console.log({
                predictedTime,
                currentDistanceInKm,
                distance: event.value.distance,
                unit: event.value.unit,
                event,
              }); */
              const normalisedPaceUnit =
                paceUnit === "mile" ? "miles" : paceUnit;
              const eventDistanceInCurrentUnit =
                event.value.unit === normalisedPaceUnit
                  ? event.value.distance
                  : normalisedPaceUnit === "km"
                  ? eventDistanceInKm
                  : // pace must be miles but event km or m
                  event.value.unit === "km"
                  ? event.value.distance * 0.621371
                  : (event.value.distance * 0.621371) / 1000;

              const predictedPace = calculatePace(
                eventDistanceInCurrentUnit,
                predictedTime.hours,
                predictedTime.minutes,
                predictedTime.seconds,
                paceUnit
              );

              const confidence = calculateConfidence(
                currentDistanceInKm,
                eventDistanceInKm
              );

              return (
                <TableRow
                  key={event.label}
                  style={{
                    backgroundColor:
                      currentDistanceInKm === eventDistanceInKm
                        ? "#f0f0f0"
                        : "inherit",
                  }}
                >
                  <TableCell component="th" scope="row">
                    {event.label}
                  </TableCell>
                  <TableCell align="left">
                    {predictedTime.hours}h {predictedTime.minutes}m{" "}
                    {predictedTime.seconds}s
                  </TableCell>
                  <TableCell align="left">
                    {predictedPace.paceMinutesCalc.toString().padStart(2, "0")}:
                    {predictedPace.paceSecondsCalc.toString().padStart(2, "0")}{" "}
                    / {paceUnit}
                  </TableCell>
                  <TableCell align="right">{confidence}%</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default RaceTimePredictor;
